/**
 * src/lib/referral-code.ts
 *
 * Captures ?ref=<code> off the registration URL and holds it until the user
 * actually submits the form.
 *
 * WHY sessionStorage AND NOT localStorage
 * ---------------------------------------
 * Per the product decision: a visitor who arrives via a referral link gets
 * attributed for that visit. If they come back later through the same link,
 * the code is in the URL again and gets captured again. If they come back
 * without the link, they are a direct signup.
 *
 * sessionStorage matches that exactly — it survives page reloads and the
 * multi-step registration flow within a tab, and clears when the tab closes.
 * localStorage would keep attributing signups to a referrer weeks after the
 * fact, on a shared exhibition tablet, to whoever happened to use it first.
 *
 * WHY CAPTURED AT BOOTSTRAP, NOT INSIDE RegisterForm
 * --------------------------------------------------
 * The router can move a visitor before any form mounts — RoleHomeRedirect
 * sends signed-out users from "/" to /event-mode, and guards can redirect
 * too. Any of those drops the query string. Reading it once, from
 * window.location, before React renders, means the code survives whatever
 * the router does next.
 */

const STORAGE_KEY = "bp_referral_code";

/** Server accepts A-Z0-9, 4-10 chars. Anything else is ignored rather than
 *  sent — a malformed code would just be discarded server-side anyway. */
function normalise(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined;
  const code = raw.trim().toUpperCase();
  return /^[A-Z0-9]{4,10}$/.test(code) ? code : undefined;
}

/**
 * Call once at app bootstrap, before render. Safe to call repeatedly.
 * A ref in the current URL always replaces a stored one — the most recent
 * link a visitor followed is the one that should get credit.
 */
export function captureReferralFromUrl(): void {
  try {
    const fromUrl = normalise(new URLSearchParams(window.location.search).get("ref"));
    if (fromUrl) window.sessionStorage.setItem(STORAGE_KEY, fromUrl);
  } catch {
    // Private browsing or storage disabled. Attribution is a nice-to-have;
    // never let it break the page.
  }
}

/** The code to send with the registration payload, if any. */
export function getReferralCode(): string | undefined {
  try {
    return normalise(window.sessionStorage.getItem(STORAGE_KEY));
  } catch {
    return undefined;
  }
}

/** Call after a successful registration so a second signup in the same tab
 *  is not credited to the same referrer. */
export function clearReferralCode(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** For the "you were referred by a friend" note on the register screen. */
export function hasReferralCode(): boolean {
  return getReferralCode() !== undefined;
}
